import { useEffect, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// History for an exercise across the user's previous sessions.
// Returns:
//   recentSessions — array of the most-recent 3 sessions that
//                    included this exercise, newest first. Each
//                    entry is { date, sets: [{ weight_kg, reps }] }
//                    with sets in entry order (set 1 → set N) and
//                    warmups excluded. Drives the "Last 3 sessions"
//                    history strip above the set inputs so the user
//                    can read off their recent progression while
//                    deciding today's weight.
//   lastSets       — ordered non-warmup sets from the most recent
//                    session — alias for recentSessions[0].sets,
//                    kept for the per-set "last time" hints inside
//                    SetLogger.
//   lastSet        — heaviest set across the most recent session.
//                    Backwards-compatible headline value.
//   loading        — fetch state.
//
// RLS limits results to the current user's data, so no explicit
// user_id filter is needed on the query.
export function useExerciseHistory(exerciseId) {
  const { user } = useAuth()
  const [lastSet, setLastSet] = useState(null)
  const [lastSets, setLastSets] = useState([])
  const [recentSessions, setRecentSessions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!supabaseConfigured || !user || !exerciseId) {
      setLastSet(null)
      setLastSets([])
      setRecentSessions([])
      setLoading(false)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    ;(async () => {
      // Catalogue has duplicate rows per exercise name (a prior sync
      // left ~2-3 rows for most exercises with different uuids). If
      // we query exercise_logs by the literal exercise_id of the row
      // the user just picked, we miss history written against any
      // of the sibling rows with the same name. So: resolve the
      // name first, then expand to the full set of ids that share
      // it, then query logs across all of them.
      const { data: target } = await supabase
        .from('exercises')
        .select('name')
        .eq('id', exerciseId)
        .maybeSingle()
      if (cancelled) return
      let candidateIds = [exerciseId]
      if (target?.name) {
        const { data: siblings } = await supabase
          .from('exercises')
          .select('id')
          .eq('name', target.name)
        if (cancelled) return
        if (Array.isArray(siblings) && siblings.length) {
          candidateIds = siblings.map((s) => s.id)
        }
      }
      // sets has no created_at — order via set_number (the column
       // that exists for this exact purpose). Asking for created_at
       // here used to silently 400 the whole select and zero out
       // the result, so every exercise lit up as "no history".
      const { data, error } = await supabase
        .from('exercise_logs')
        .select('id, sessions!inner(date, created_at), sets(weight_kg, reps, is_warmup, id, set_number)')
        .in('exercise_id', candidateIds)
        .limit(20)
      if (cancelled) return
      if (error || !Array.isArray(data) || data.length === 0) {
        setLastSet(null)
        setLastSets([])
        setRecentSessions([])
        setLoading(false)
        return
      }
      // Sort the exercise_logs newest-first across sessions, then
      // collapse the most-recent 3 into a compact shape the UI can
      // render directly.
      const sorted = [...data].sort((a, b) => {
        const da = a.sessions?.date || ''
        const db = b.sessions?.date || ''
        if (db !== da) return db.localeCompare(da)
        const ca = a.sessions?.created_at || ''
        const cb = b.sessions?.created_at || ''
        return cb.localeCompare(ca)
      })
      const orderSets = (sets) => {
        const filtered = (sets || []).filter((s) => !s.is_warmup)
        return filtered.sort((a, b) => {
          const sa = Number(a.set_number)
          const sb = Number(b.set_number)
          if (Number.isFinite(sa) && Number.isFinite(sb) && sa !== sb) return sa - sb
          return String(a.id || '').localeCompare(String(b.id || ''))
        })
      }
      const recent = sorted.slice(0, 3).map((log) => {
        const setsOrdered = orderSets(log.sets)
        return {
          date: log.sessions?.date || null,
          sets: setsOrdered.map((s) => ({
            weight_kg: Number(s.weight_kg),
            reps: Number(s.reps),
          })),
        }
      }).filter((s) => s.sets.length > 0)
      setRecentSessions(recent)

      const latest = recent[0]
      if (!latest || latest.sets.length === 0) {
        setLastSet(null)
        setLastSets([])
      } else {
        const top = latest.sets.reduce((a, b) => (b.weight_kg > a.weight_kg ? b : a))
        setLastSet({ weight_kg: top.weight_kg, reps: top.reps })
        setLastSets(latest.sets)
      }
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [exerciseId, user])

  return { lastSet, lastSets, recentSessions, loading }
}
